/**
 * ForteHealth Help Center content.
 *
 * Static, version-controlled FAQ + how-to library. Stored as typed data
 * (not markdown files) so:
 *   - links stay in-sync with the actual app routes (TS-checked)
 *   - search is trivially `.filter()` against title + body
 *   - no markdown parser ships in the bundle
 *   - product/care/legal can edit content via PRs with full review
 *
 * Block types support a small superset of plain text so authors can write
 * lists, callouts, and intra-portal links without learning markdown. See
 * <ArticleRenderer/> in src/routes/HelpArticle.tsx for the renderer.
 *
 * Inline text in `text` fields supports two patterns:
 *   **bold**          → <strong>
 *   [label](href)     → <a> or <Link>, auto-routed if href starts with "/"
 */

export type HelpBlock =
  | { type: "p"; text: string }
  | { type: "h"; text: string }
  | { type: "ul"; items: string[] }
  | { type: "ol"; items: string[] }
  | { type: "note"; tone: "info" | "warning" | "success"; text: string };

export interface HelpArticle {
  id: string;             // URL slug
  title: string;
  summary: string;        // 1-line teaser used on index + search results
  category: HelpCategoryId;
  keywords?: string[];    // boost search hits beyond title/body terms
  body: HelpBlock[];
}

export type HelpCategoryId =
  | "getting-started"
  | "treatments"
  | "service"
  | "refills"
  | "billing"
  | "shipping"
  | "labs"
  | "account"
  | "privacy";

export interface HelpCategory {
  id: HelpCategoryId;
  label: string;
  blurb: string;
}

export const HELP_CATEGORIES: HelpCategory[] = [
  { id: "getting-started", label: "Getting started",        blurb: "What ForteHealth is, who can use it, what to expect from your first visit." },
  { id: "treatments",      label: "Treatments",             blurb: "Weight-loss (semaglutide/tirzepatide), TRT, dosing, side effects, missed doses." },
  { id: "service",         label: "Service & care team",    blurb: "How messaging works, response times, when to call 911, who you'll meet." },
  { id: "refills",         label: "Refills",                blurb: "How the monthly refill check-in works and what happens if you miss it." },
  { id: "billing",         label: "Billing & subscriptions", blurb: "Pricing, invoices, pausing, canceling, refunds, promo codes." },
  { id: "shipping",        label: "Shipping & pharmacy",    blurb: "Timelines, signature requirements, cold-chain, lost packages." },
  { id: "labs",            label: "Lab tests",              blurb: "Which tests we order, how to read TRT panels, turnaround times." },
  { id: "account",         label: "Account & technical",    blurb: "Sign-in trouble, password reset, browser issues, multi-factor authentication." },
  { id: "privacy",         label: "Privacy & security",     blurb: "HIPAA, what data we collect, who sees it, how to share with family." },
];

// ---------------------------------------------------------------------------
// Articles. Ordered roughly by importance within each category — the Help
// index lists them in this order. Keep titles question-shaped where it makes
// sense (matches what patients type into search).
// ---------------------------------------------------------------------------

export const HELP_ARTICLES: HelpArticle[] = [
  // ──────────────────────────────  GETTING STARTED  ──────────────────────────────
  {
    id: "what-is-fortehealth",
    title: "What is ForteHealth?",
    category: "getting-started",
    summary: "A cash-pay telehealth platform for weight-loss (GLP-1) and testosterone optimization (TRT), delivered to your door.",
    keywords: ["about", "who", "company", "telehealth"],
    body: [
      { type: "p", text: "ForteHealth is a direct-to-consumer telehealth platform that pairs you with a licensed clinician for two specialties: medically supervised **weight management** (compounded semaglutide and tirzepatide) and **testosterone optimization for men (TRT)**." },
      { type: "p", text: "Everything happens online — your intake, your video visit with the clinician, your prescription, your shipments, and your ongoing check-ins all live in this portal. There's no clinic to drive to and no insurance to fight." },
      { type: "p", text: "We're cash-pay, which means you'll never see a surprise bill. The price you see at checkout is the price you pay." },
    ],
  },
  {
    id: "is-this-right-for-me",
    title: "Is ForteHealth right for me?",
    category: "getting-started",
    summary: "Eligibility basics — who we can treat, who we have to refer elsewhere.",
    body: [
      { type: "p", text: "ForteHealth is for adults (18+) in U.S. states where we're licensed. To prescribe, the clinician needs a complete intake, recent lab work for TRT (we order this for you), and — for some plans — a brief video visit." },
      { type: "h", text: "You may not be a fit if…" },
      { type: "ul", items: [
        "You're pregnant, planning to become pregnant, or breastfeeding (no GLP-1).",
        "You have a personal or family history of medullary thyroid cancer or MEN-2 (no GLP-1).",
        "You have untreated severe pancreatitis or active eating disorder (no GLP-1).",
        "You have prostate or breast cancer, severe untreated sleep apnea, or hematocrit >54% (no TRT until cleared).",
        "You're under 18.",
      ]},
      { type: "p", text: "If you start an intake and the clinician determines you're not a candidate, you won't be charged for the medication and we'll explain why and what to do next." },
    ],
  },
  {
    id: "first-visit-what-to-expect",
    title: "What to expect from your first visit",
    category: "getting-started",
    summary: "From intake to your first shipment, here's the timeline and what each step involves.",
    body: [
      { type: "ol", items: [
        "**Intake** (10–15 min): you'll answer health-history questions and confirm your medications, allergies, and goals.",
        "**Labs** (TRT only — skip for weight-loss): we order a lab order to a Quest or Labcorp location near you. Free to do; results come back in 2–5 business days.",
        "**Clinician review** (1–2 business days): a licensed clinician reviews your intake (and labs, for TRT) and either approves your plan, asks a clarifying question, or schedules a video visit.",
        "**Video visit** (if needed, 15–20 min): scheduled through this portal at a time that works for you.",
        "**Prescription & shipment**: once approved, your medication ships from our partner pharmacy in 3–7 business days. You'll get tracking by email and in [Dashboard](/dashboard).",
      ]},
      { type: "note", tone: "info", text: "Your full intake answers, prescription, and shipment status are always available under [My Treatment](/treatment)." },
    ],
  },

  // ──────────────────────────────  TREATMENTS  ──────────────────────────────
  {
    id: "glp1-semaglutide-vs-tirzepatide",
    title: "Semaglutide vs. tirzepatide — what's the difference?",
    category: "treatments",
    summary: "Both are GLP-1 agonists used for weight loss. Tirzepatide adds a second receptor (GIP) and tends to produce more weight loss but costs more.",
    keywords: ["ozempic", "wegovy", "zepbound", "mounjaro", "glp1", "weight loss"],
    body: [
      { type: "p", text: "**Semaglutide** is a GLP-1 receptor agonist — it slows stomach emptying and increases satiety, which reduces calorie intake. It's the active ingredient in Ozempic and Wegovy. In trials, average weight loss is roughly 14–15% of body weight over 68 weeks." },
      { type: "p", text: "**Tirzepatide** is a dual GLP-1 and GIP agonist — it acts on two receptors instead of one. It's the active ingredient in Mounjaro and Zepbound. Average weight loss in trials is roughly 20–22% over 72 weeks." },
      { type: "p", text: "ForteHealth offers **compounded** versions of both, prepared by a U.S. 503A or 503B licensed pharmacy. Compounding lets us offer a personalized dose and avoid the supply shortages that have affected the brand-name versions." },
      { type: "p", text: "Your clinician will recommend a starting molecule based on your goals, medical history, side-effect tolerance, and budget. You can switch later if needed." },
    ],
  },
  {
    id: "glp1-side-effects",
    title: "What are common GLP-1 side effects?",
    category: "treatments",
    summary: "Most side effects are GI (nausea, constipation, reflux) and resolve as your body adapts. Here's what's normal and what isn't.",
    keywords: ["nausea", "vomiting", "side effects", "constipation"],
    body: [
      { type: "h", text: "Common (usually resolve in 1–4 weeks)" },
      { type: "ul", items: [
        "Nausea, especially in the first 24–48 hours after your weekly dose",
        "Constipation or diarrhea",
        "Heartburn / reflux",
        "Decreased appetite (this is partly the point — but if it's extreme, message your clinician)",
        "Fatigue or feeling cold",
      ]},
      { type: "h", text: "Less common but call us" },
      { type: "ul", items: [
        "Severe, persistent abdominal pain (could be pancreatitis)",
        "Vomiting you can't keep fluids down",
        "Signs of gallbladder issues (right-upper-belly pain, especially after fatty meals)",
        "Vision changes",
      ]},
      { type: "note", tone: "warning", text: "**Always 911 for**: severe allergic reaction (face/throat swelling, trouble breathing), severe chest pain, or signs of stroke." },
      { type: "p", text: "Tips that help most patients: eat smaller meals, drink water through the day, avoid heavy/fatty meals the day of injection, and prioritize protein + fiber. Your [care team](/messages) can adjust your dose or pause titration if side effects are tough." },
    ],
  },
  {
    id: "missed-dose-glp1",
    title: "What if I miss a weekly GLP-1 dose?",
    category: "treatments",
    summary: "If it's within 5 days, take it as soon as you remember. If it's longer, skip it and resume your normal schedule.",
    body: [
      { type: "ul", items: [
        "**If less than 5 days late**: inject as soon as you remember, then continue your regular weekly schedule (you can shift your weekly day if you want — just stay at least 3 days between doses).",
        "**If more than 5 days late**: skip the missed dose and take your next dose on its normal day.",
        "**If you've missed 2+ weeks in a row**: message your clinician before resuming — they may want you to restart at a lower dose to avoid bringing back side effects.",
      ]},
      { type: "p", text: "Doubling up to 'catch up' is not safe — it can cause severe nausea and vomiting." },
    ],
  },
  {
    id: "trt-protocol",
    title: "What does a TRT protocol look like?",
    category: "treatments",
    summary: "Typically weekly intramuscular or subcutaneous injections of testosterone cypionate, sometimes paired with HCG and/or an aromatase inhibitor.",
    keywords: ["testosterone", "cypionate", "hcg", "anastrozole", "ai"],
    body: [
      { type: "p", text: "The most common ForteHealth TRT protocol is **weekly testosterone cypionate** delivered subcutaneously (under the skin) with a small insulin-style needle. Some patients prefer intramuscular injections — both work; subQ tends to be more comfortable." },
      { type: "p", text: "Depending on your labs, fertility goals, and how your body responds, your clinician may add:" },
      { type: "ul", items: [
        "**HCG** (human chorionic gonadotropin) — preserves testicular function and fertility while on TRT.",
        "**Anastrozole** (an aromatase inhibitor) — controls estrogen if it climbs too high. Most men don't need this; over-suppressing estrogen causes its own problems.",
        "**Enclomiphene** — sometimes used instead of TRT for men who want to preserve fertility.",
      ]},
      { type: "p", text: "Your starting dose is based on your baseline labs and symptoms. We re-check labs at 6 weeks and again at 6 months to titrate." },
      { type: "note", tone: "info", text: "All of this is discussed in your video visit so you can decide together. There's no obligation to do anything beyond the core protocol." },
    ],
  },
  {
    id: "injection-instructions",
    title: "How do I do my injection?",
    category: "treatments",
    summary: "Step-by-step injection technique, including what to do if you draw air or bend a needle.",
    body: [
      { type: "p", text: "Your shipment includes a detailed printed guide and your clinician will walk you through the first injection on a video visit if you'd like. The short version:" },
      { type: "ol", items: [
        "Wash your hands; gather your vial, syringe, alcohol swab, and a sharps container.",
        "Wipe the rubber stopper of the vial with alcohol; let it dry.",
        "Draw your prescribed dose into the syringe. Tap to dislodge bubbles; press the plunger to expel them back into the vial.",
        "Choose a site (abdomen, thigh, or upper-outer glute for subQ; deltoid or glute for IM); clean with alcohol; let dry.",
        "Pinch a fold of skin (subQ) or stretch it taut (IM). Insert needle, push the plunger steadily, withdraw.",
        "Press the spot briefly with gauze; bandage if it bleeds; dispose of the needle in your sharps container.",
      ]},
      { type: "p", text: "**Rotate sites** so you don't develop lumps or irritation in one spot. A simple rotation: left abdomen → right abdomen → left thigh → right thigh." },
      { type: "p", text: "If you bend a needle, draw blood, can't finish a dose, or feel unsure about something — [message your care team](/messages). We'd rather answer five questions than have you guess." },
    ],
  },

  // ──────────────────────────────  SERVICE & CARE TEAM  ──────────────────────────────
  {
    id: "messaging-response-time",
    title: "How fast does the care team reply?",
    category: "service",
    summary: "Within 1 business day for most messages. Clinical questions go straight to a licensed clinician; billing and account questions go to support.",
    keywords: ["response", "reply", "wait", "how long"],
    body: [
      { type: "p", text: "Most messages are answered within **1 business day** (Mon–Fri, 9 AM–6 PM ET). Many are answered within a few hours." },
      { type: "p", text: "When you write a new message in [Messages](/messages), you'll pick one of three categories:" },
      { type: "ul", items: [
        "**Medical** — routed to a licensed clinician. Use this for symptoms, dosing, side effects, or anything about your prescription.",
        "**Support** — routed to the ForteHealth care team. Use this for shipping, account, or anything not strictly clinical.",
        "**Billing** — routed to billing. Use this for invoices, refunds, subscription changes.",
      ]},
      { type: "note", tone: "warning", text: "Messaging is **not for emergencies**. For chest pain, trouble breathing, severe allergic reaction, suicidal thoughts, or any life-threatening symptom — call **911** or go to your nearest ER." },
    ],
  },
  {
    id: "video-visit-prep",
    title: "How do I prepare for a video visit?",
    category: "service",
    summary: "Test your camera and mic, have your medication list ready, and pick a private quiet spot.",
    body: [
      { type: "ul", items: [
        "**Browser**: Chrome, Safari, or Edge — current version. Firefox usually works but Chrome/Safari are most reliable.",
        "**Permissions**: when prompted, allow camera + microphone for portal.fortehealth.co.",
        "**Quiet, private space**: visits cover health info; pick somewhere you can speak openly.",
        "**Have ready**: a current list of medications and supplements, any recent lab results, and 2–3 specific questions you want to cover.",
        "**Bandwidth**: 1–2 Mbps up/down is plenty. If you're on hotel Wi-Fi or a weak cellular signal, try to switch.",
      ]},
      { type: "p", text: "If technology fails during your visit, the clinician will call you on the phone number on file to finish the consult. You won't be re-charged or have to reschedule from scratch." },
    ],
  },
  {
    id: "after-hours-emergencies",
    title: "What do I do after hours or in an emergency?",
    category: "service",
    summary: "911 for emergencies, your local urgent care or PCP for urgent-but-not-emergency issues. Portal messages are read the next business day.",
    body: [
      { type: "note", tone: "warning", text: "**Always 911 for life-threatening symptoms**: chest pain, trouble breathing, signs of stroke (face droop, slurred speech, arm weakness), severe allergic reaction, severe abdominal pain, or suicidal thoughts." },
      { type: "p", text: "For **urgent but not emergency** issues (e.g., persistent vomiting, fever above 102, injection-site infection signs) outside business hours: visit an **urgent care** or call your **primary care provider**." },
      { type: "p", text: "For non-urgent questions: send a message in [Messages](/messages). We'll respond the next business day." },
    ],
  },
  {
    id: "who-is-on-my-care-team",
    title: "Who's on my care team?",
    category: "service",
    summary: "A licensed clinician (MD, DO, NP, or PA) plus a small support team. You'll see who replied at the top of each message.",
    body: [
      { type: "p", text: "Your **prescribing clinician** is a U.S.-licensed MD, DO, NP, or PA in a state where you reside. They review your intake, write your prescription, and respond to medical questions." },
      { type: "p", text: "Our **care team** handles shipping, scheduling, account questions, and general support. They can't make clinical decisions but they escalate to your clinician immediately when needed." },
      { type: "p", text: "Every message in your thread shows the sender's name and role, so you always know whether you're hearing from your clinician or from care." },
    ],
  },

  // ──────────────────────────────  REFILLS  ──────────────────────────────
  {
    id: "how-refills-work",
    title: "How does the monthly refill check-in work?",
    category: "refills",
    summary: "Each month before your next shipment, you'll fill out a 2-minute check-in. Your clinician reviews it and approves the refill.",
    body: [
      { type: "p", text: "Federal regulations and good medicine both require that we check in with you before sending more medication. The portal does this automatically:" },
      { type: "ol", items: [
        "About 7 days before your next shipment, you'll get an email + a portal notification asking you to complete a **check-in**.",
        "The check-in is short (~2 minutes): how's your weight trending, any side effects, anything new in your meds or health.",
        "Your clinician reviews your responses (usually same business day) and approves the refill.",
        "Your pharmacy ships in 3–7 business days.",
      ]},
      { type: "p", text: "You can complete the check-in any time before your shipment is due — there's no penalty for being early. You'll get reminder emails at T-7, T-3, and weekly until you complete it." },
    ],
  },
  {
    id: "missed-refill-checkin",
    title: "I missed my refill check-in — now what?",
    category: "refills",
    summary: "Complete the check-in any time. We'll resume your shipment cycle from there. We don't auto-cancel for missed check-ins.",
    body: [
      { type: "p", text: "Nothing bad happens. The check-in just sits in your queue until you complete it. Open [Dashboard](/dashboard) and you'll see the check-in card at the top." },
      { type: "p", text: "**Your subscription does NOT auto-cancel** for a missed check-in. Your next shipment is paused until the clinician approves, but as soon as you finish the check-in and it's approved, the shipment goes out and your billing resumes its normal cadence." },
      { type: "p", text: "If you want to truly pause or cancel, manage your subscription in [Billing](/billing) instead." },
    ],
  },

  // ──────────────────────────────  BILLING & SUBSCRIPTIONS  ──────────────────────────────
  {
    id: "how-billing-works",
    title: "How does ForteHealth billing work?",
    category: "billing",
    summary: "You're billed when your subscription renews — monthly, every 12 weeks, or every 24 weeks depending on the plan you chose.",
    body: [
      { type: "p", text: "ForteHealth is cash-pay (no insurance involved). The price you see at checkout is the price you pay; there are no surprise bills." },
      { type: "h", text: "Your cadence depends on the plan" },
      { type: "ul", items: [
        "**Monthly weight-loss plan**: billed every 30 days; one month of medication per shipment.",
        "**3-month weight-loss plan (12-week)**: billed every 84 days; three months shipped at once for a lower per-month rate.",
        "**6-month weight-loss plan (24-week)**: billed every 168 days; six months shipped at once for the best per-month rate.",
        "**TRT diagnostic bundle**: a one-time charge that includes labs + the video visit. If you're approved, your TRT medication subscription begins separately.",
        "**TRT subscription**: billed monthly once you're approved.",
      ]},
      { type: "p", text: "View all your charges, download receipts, or update your card in [Billing](/billing)." },
    ],
  },
  {
    id: "cancel-or-pause",
    title: "How do I cancel or pause my subscription?",
    category: "billing",
    summary: "Cancel from Account Settings any time. We don't lock you in. Cancellation stops the next renewal — your current cycle continues.",
    body: [
      { type: "p", text: "Go to [Account Settings](/account) and click **Cancel subscription**. You'll be asked a quick reason (so we can improve) and confirm. Cancellation takes effect at the end of your current paid cycle — you keep what you've already paid for." },
      { type: "p", text: "There's no penalty, no lock-in, and no need to call." },
      { type: "p", text: "Want to **pause instead**? Right now we don't have a self-serve pause — message [Support](/messages) and we'll skip your next shipment. Most patients ask for a 4–8 week skip; longer pauses we treat like a cancel-and-resume so we can re-confirm your dose is still right." },
    ],
  },
  {
    id: "refund-policy",
    title: "What's the refund policy?",
    category: "billing",
    summary: "Unopened, unshipped orders: full refund. Shipped medication is non-returnable. Discretionary refunds for genuine issues.",
    body: [
      { type: "p", text: "Our full refund policy lives in our [Terms of Service](https://fortehealth.co/terms). The short version:" },
      { type: "ul", items: [
        "**Before your medication ships**: full refund, no questions asked. Message [Support](/messages).",
        "**After your medication ships**: medication is non-returnable for safety reasons (we can't legally re-sell anything that's left our pharmacy).",
        "**Lab fees**: refundable up until your lab order is generated.",
        "**Video visit fees**: refundable if the visit hasn't happened yet.",
      ]},
      { type: "p", text: "Even outside the strict policy, we issue discretionary refunds for genuine product issues — a damaged vial, a missing component, the wrong item shipped. Reach out — we'd much rather make it right than have you stew." },
    ],
  },
  {
    id: "promo-codes",
    title: "Do you have promo codes?",
    category: "billing",
    summary: "Yes — applied at checkout. We also run discounts for first-time patients, referrals, and milestone retention.",
    body: [
      { type: "p", text: "Promo codes can be entered at checkout in the **Add promotion code** box just above the **Pay** button. If you have one and forgot to enter it, [message support](/messages) within 7 days of the charge and we'll apply it retroactively." },
      { type: "p", text: "Three other ways to save:" },
      { type: "ul", items: [
        "**Longer plans cost less per month** — the 12-week and 24-week weight-loss plans are 15–25% cheaper per month than monthly.",
        "**Referrals**: when a friend signs up using your referral link, you both get a credit.",
        "**Milestone retention**: long-term patients (6+ months) automatically receive periodic loyalty discounts.",
      ]},
    ],
  },
  {
    id: "invoices-receipts",
    title: "Where do I find my invoices and receipts?",
    category: "billing",
    summary: "All invoices are in the Billing page, downloadable as PDF.",
    body: [
      { type: "p", text: "Open [Billing](/billing) and scroll to **Invoices**. Each line shows the date, amount, and a download link for the PDF receipt. Receipts include the line items, the payment method (card type + last four), and ForteHealth's mailing address — what you need for FSA/HSA reimbursement." },
      { type: "p", text: "**FSA / HSA**: weight-loss medication and TRT generally qualify for FSA/HSA reimbursement when prescribed for a diagnosed medical condition. We're not your tax advisor, but the receipts have everything your plan administrator typically asks for." },
    ],
  },

  // ──────────────────────────────  SHIPPING & PHARMACY  ──────────────────────────────
  {
    id: "shipping-timelines",
    title: "How long does shipping take?",
    category: "shipping",
    summary: "Usually 3–7 business days from prescription approval to your doorstep. We use overnight cold-chain shipping for temperature-sensitive medication.",
    body: [
      { type: "p", text: "From the moment your clinician approves your prescription, expect:" },
      { type: "ul", items: [
        "**1–2 business days** for the pharmacy to compound and prepare your order.",
        "**1–3 business days** in transit, depending on your distance from the pharmacy.",
        "**Refrigerated medications** (most GLP-1 vials and HCG) ship **overnight** with cold packs once they leave the pharmacy.",
      ]},
      { type: "p", text: "You'll get a tracking link by email and on your [Dashboard](/dashboard) when the shipment leaves the pharmacy." },
    ],
  },
  {
    id: "signature-controlled-substance",
    title: "Do I need to be home to sign? (TRT)",
    category: "shipping",
    summary: "Yes for TRT (DEA Schedule III requires adult signature). No for GLP-1.",
    body: [
      { type: "p", text: "**Testosterone is a DEA Schedule III controlled substance**, which means federal law requires an **adult signature** at delivery. We can't leave it at the door, even if you have a delivery instruction set." },
      { type: "p", text: "Tips:" },
      { type: "ul", items: [
        "Ship to a work address (front desk often signs)",
        "Sign up for FedEx Delivery Manager or UPS My Choice to redirect to a pickup location",
        "Use a vacation hold if you'll be away when the next dose ships",
      ]},
      { type: "p", text: "GLP-1 medications are **not** controlled substances and don't require a signature, though we still recommend ensuring you're home to refrigerate the package promptly." },
    ],
  },
  {
    id: "lost-or-damaged-package",
    title: "My package is lost, damaged, or stuck in transit",
    category: "shipping",
    summary: "Message support immediately. We'll work with the carrier and replace if needed — at no cost to you when it's our or the carrier's fault.",
    body: [
      { type: "p", text: "If your tracking has been stuck for more than 2 business days, the package shows delivered but you don't have it, or you've received it visibly damaged: [message Support](/messages) right away and include your order number." },
      { type: "p", text: "We'll open a carrier claim and, if it can't be resolved in 1–2 business days, ship a replacement. You won't be re-charged for replacements when the loss or damage is the pharmacy's or carrier's responsibility." },
      { type: "note", tone: "warning", text: "If a refrigerated package was delivered but sat in heat for several hours and the cold pack is warm to the touch, do **not** use the medication. Take a photo and message us." },
    ],
  },
  {
    id: "storage-instructions",
    title: "How should I store my medication?",
    category: "shipping",
    summary: "GLP-1 in the fridge until first use; testosterone at room temperature, out of direct light.",
    body: [
      { type: "h", text: "GLP-1 (semaglutide, tirzepatide)" },
      { type: "ul", items: [
        "Refrigerate (36–46°F / 2–8°C) until first use.",
        "After first use, may be kept at room temperature (up to 86°F / 30°C) for the beyond-use date printed on the vial (typically 28–56 days).",
        "Do not freeze. If accidentally frozen, discard.",
        "Keep in original packaging to protect from light.",
      ]},
      { type: "h", text: "Testosterone cypionate" },
      { type: "ul", items: [
        "Room temperature (68–77°F / 20–25°C) is fine.",
        "Avoid direct sunlight and high heat (don't store in a car or bathroom).",
        "If the oil looks cloudy or has crystallized, warm the vial gently in your hand for a few minutes — that usually resolves it. If it doesn't, [message Support](/messages).",
      ]},
    ],
  },

  // ──────────────────────────────  LAB TESTS  ──────────────────────────────
  {
    id: "what-labs-for-trt",
    title: "What lab tests do you order for TRT?",
    category: "labs",
    summary: "A baseline TRT panel covers total + free testosterone, estradiol, hematocrit, PSA, LH/FSH, and a metabolic panel.",
    body: [
      { type: "p", text: "Before starting TRT, we order:" },
      { type: "ul", items: [
        "**Total testosterone** — the standard measure of T levels.",
        "**Free testosterone** — the biologically active portion.",
        "**Estradiol (sensitive)** — to baseline your estrogen so we can monitor it on therapy.",
        "**Hematocrit / CBC** — TRT can raise red blood cell count; we monitor for this.",
        "**PSA** (Prostate Specific Antigen) — baseline before starting any androgen.",
        "**LH and FSH** — your pituitary hormones, to understand whether your low T is primary (testicular) or secondary (pituitary).",
        "**Comprehensive Metabolic Panel** — kidney, liver, glucose baseline.",
      ]},
      { type: "p", text: "We re-run a slimmer version (testosterone, estradiol, hematocrit, PSA) at week 6 and then every 6 months on therapy." },
    ],
  },
  {
    id: "how-to-read-labs",
    title: "How do I read my lab results?",
    category: "labs",
    summary: "Open Labs, tap a panel, and you'll see each value with a reference range and a green/amber/red status. Your clinician sends a plain-English interpretation.",
    body: [
      { type: "p", text: "Open [Labs](/labs) to see every panel we've ever ordered for you. Tap a panel to see each result with its **reference range** and a colored bar:" },
      { type: "ul", items: [
        "**Green (in range)** — within the normal reference range.",
        "**Amber (low or high)** — outside the range but not yet critical.",
        "**Red (critical)** — significantly out of range; your clinician will reach out.",
      ]},
      { type: "p", text: "Reference ranges are LOINC-coded and lab-specific. Your clinician will send you a plain-English message interpreting the results in the context of your symptoms — the numbers alone don't tell the full story." },
    ],
  },
  {
    id: "lab-turnaround",
    title: "When will I get my lab results back?",
    category: "labs",
    summary: "Most results are back within 2–5 business days from when blood is drawn.",
    body: [
      { type: "p", text: "Most blood draws done at Quest or Labcorp post results in **2–5 business days**. We post them to [Labs](/labs) as soon as the lab releases them; you'll get an email + portal notification." },
      { type: "p", text: "Your clinician reviews the results within 1 business day of release and either approves your next step (start, continue, or adjust your protocol) or messages you with questions." },
    ],
  },

  // ──────────────────────────────  ACCOUNT & TECHNICAL  ──────────────────────────────
  {
    id: "reset-password",
    title: "How do I reset my password?",
    category: "account",
    summary: "Click 'Forgot password' on the sign-in screen. You'll get a reset link by email within a minute.",
    body: [
      { type: "ol", items: [
        "Go to the [sign-in page](/login) and click **Forgot password**.",
        "Enter the email you signed up with.",
        "Check your inbox (and spam folder) for an email from no-reply@fortehealth.co with a reset link.",
        "Click the link and set a new password.",
      ]},
      { type: "p", text: "Reset links expire after 60 minutes. If yours expires, just request a new one." },
      { type: "note", tone: "info", text: "If you signed up with a typo in your email, you won't get the reset link. [Message Support](/messages) from any email — include your full name and date of birth so we can verify and update the address." },
    ],
  },
  {
    id: "change-email-or-phone",
    title: "How do I change my email or phone number?",
    category: "account",
    summary: "Update phone in Account Settings instantly; email changes require a quick verification on the new address.",
    body: [
      { type: "p", text: "Open [Account Settings](/account)." },
      { type: "ul", items: [
        "**Phone**: update in place. You'll get a one-time code on the new number to confirm.",
        "**Email**: update in place. You'll get a verification link at the new address. Until you click it, your old email stays active for sign-in and notifications.",
        "**Mailing / shipping address**: update in place. Future shipments use the new address; in-flight shipments keep the old one.",
      ]},
    ],
  },
  {
    id: "browser-issues",
    title: "The portal isn't loading or video isn't working",
    category: "account",
    summary: "Try the latest Chrome or Safari, allow camera/mic permissions, and disable strict tracker blockers for portal.fortehealth.co.",
    body: [
      { type: "ol", items: [
        "**Update your browser** to the latest version (Chrome, Safari, Edge, or Firefox).",
        "**Hard refresh**: Cmd-Shift-R (Mac) or Ctrl-Shift-R (Windows). This clears the cached version.",
        "**Permissions**: when prompted, allow camera + microphone for portal.fortehealth.co.",
        "**Tracker blockers / privacy extensions**: extensions like uBlock Origin or Privacy Badger can block our session or Stripe checkout. Disable them just for portal.fortehealth.co.",
        "**Incognito / Private window**: a quick way to rule out a browser extension conflict.",
        "**Different network**: corporate VPNs sometimes block video. Try mobile hotspot to test.",
      ]},
      { type: "p", text: "Still broken? [Message Support](/messages) with your browser version (in the address bar, type chrome://version or about:version) and what you were trying to do." },
    ],
  },
  {
    id: "two-factor-authentication",
    title: "How do I enable two-factor authentication?",
    category: "account",
    summary: "We're rolling out optional patient MFA soon. Admin accounts already require MFA.",
    body: [
      { type: "p", text: "Optional patient-side two-factor authentication is on our roadmap for 2026. We'll email you when it's available." },
      { type: "p", text: "Until then, your portal is protected by a long-session login plus a 15-minute idle timeout (you're automatically signed out after 15 minutes of inactivity). All traffic is HTTPS, all data is encrypted at rest, and we'll never ask for your password by email or phone." },
      { type: "note", tone: "warning", text: "**ForteHealth will never** call or text asking for your password, a one-time code, or your credit card number. If someone claims to be us and asks for these, hang up and [message Support](/messages)." },
    ],
  },

  // ──────────────────────────────  PRIVACY & SECURITY  ──────────────────────────────
  {
    id: "hipaa-and-data",
    title: "How does HIPAA work at ForteHealth?",
    category: "privacy",
    summary: "ForteHealth is a HIPAA covered entity. Your PHI is encrypted, access-logged, and only shared with vendors who've signed a Business Associate Agreement.",
    keywords: ["hipaa", "privacy", "security", "data"],
    body: [
      { type: "p", text: "ForteHealth operates as a HIPAA-regulated entity. Your **Protected Health Information** (PHI) — intake answers, prescriptions, lab results, messages with your clinician — is:" },
      { type: "ul", items: [
        "Encrypted in transit (TLS 1.2+) and at rest (AES-256).",
        "Stored in HIPAA-compliant infrastructure with audit logging on every access.",
        "Only shared with vendors who have signed a **Business Associate Agreement** (BAA): our pharmacy, lab, video visit, e-signature, and email vendors.",
        "Never sold, never shared with advertisers, never used to train AI models.",
      ]},
      { type: "p", text: "Read our full Notice of Privacy Practices at [fortehealth.co/hipaa](https://fortehealth.co/hipaa)." },
    ],
  },
  {
    id: "who-can-see-my-data",
    title: "Who at ForteHealth can see my information?",
    category: "privacy",
    summary: "Your clinician, the care team supporting your account, and (only when necessary) a small group of administrators. Every access is logged.",
    body: [
      { type: "p", text: "Access is on a **need-to-know** basis:" },
      { type: "ul", items: [
        "**Your clinician** — your prescriber and any covering clinician — sees your full chart.",
        "**The care team** members handling your specific case see your account, shipping, and any messages routed to them.",
        "**A small number of operations admins** can see account-level data (not full medical records) for billing, compliance, and support escalations.",
        "**No one else.** No marketing team, no engineers (unless temporarily debugging with your explicit consent), no third-party advertisers.",
      ]},
      { type: "p", text: "Every access of your record is logged. You can request an audit log of access events at any time by emailing **privacy@fortehealth.co**." },
    ],
  },
  {
    id: "share-with-family-or-pcp",
    title: "Can I share my records with my primary care doctor or a family member?",
    category: "privacy",
    summary: "Yes — download your records from Privacy & Data Settings, or request a formal release. Family access requires a signed HIPAA authorization.",
    body: [
      { type: "p", text: "Three ways to share:" },
      { type: "ol", items: [
        "**Download your records**: open [Privacy & Data Settings](/account/privacy) and click **Download my data**. You'll get a PDF + JSON export of your full record, suitable for sharing with another provider.",
        "**Direct release to a provider**: [message Support](/messages) with the name, fax, and email of the provider you want records sent to. We'll send via secure fax or BAA-covered email.",
        "**Granting a family member access**: this requires a signed HIPAA authorization form. Request one from privacy@fortehealth.co and we'll walk you through it.",
      ]},
    ],
  },
  {
    id: "delete-my-account",
    title: "How do I delete my account?",
    category: "privacy",
    summary: "Submit a deletion request from Privacy & Data Settings. We're required by law to retain medical records for several years — we'll explain what we keep and why.",
    body: [
      { type: "p", text: "Open [Privacy & Data Settings](/account/privacy) and click **Request account deletion**." },
      { type: "p", text: "We honor the request, but federal and state regulations require us to **retain medical records for several years** (typically 7–10 depending on state). What gets deleted vs. retained:" },
      { type: "ul", items: [
        "**Deleted**: portal account access, marketing preferences, support history older than 1 year.",
        "**Retained (in encrypted, restricted storage)**: medical records (intake, prescriptions, lab results, clinical notes) for the legally required retention period.",
        "**After the retention window**: medical records are also permanently deleted.",
      ]},
      { type: "p", text: "You'll get a written confirmation when the deletion is complete (usually within 30 days)." },
    ],
  },
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

export function getArticleById(id: string): HelpArticle | undefined {
  return HELP_ARTICLES.find((a) => a.id === id);
}

export function getArticlesByCategory(categoryId: HelpCategoryId): HelpArticle[] {
  return HELP_ARTICLES.filter((a) => a.category === categoryId);
}

/**
 * Lightweight client-side search. Splits the query into terms, scores each
 * article by how many terms appear in (title, summary, keywords, body text),
 * with title hits weighted highest. Good enough for a static FAQ; we'll
 * graduate to a real search index when we're past a few hundred articles.
 */
export function searchArticles(query: string, limit = 30): HelpArticle[] {
  const q = query.trim().toLowerCase();
  if (!q) return [];
  const terms = q.split(/\s+/).filter((t) => t.length >= 2);
  if (terms.length === 0) return [];

  type Scored = { a: HelpArticle; score: number };
  const scored: Scored[] = HELP_ARTICLES.map((a) => {
    const titleL = a.title.toLowerCase();
    const summaryL = a.summary.toLowerCase();
    const keywordsL = (a.keywords ?? []).join(" ").toLowerCase();
    const bodyL = a.body
      .map((b) => ("text" in b ? b.text : "items" in b ? b.items.join(" ") : ""))
      .join(" ")
      .toLowerCase();

    let score = 0;
    for (const t of terms) {
      if (titleL.includes(t))    score += 8;
      if (summaryL.includes(t))  score += 4;
      if (keywordsL.includes(t)) score += 6;
      if (bodyL.includes(t))     score += 1;
    }
    return { a, score };
  })
    .filter((s) => s.score > 0)
    .sort((x, y) => y.score - x.score);

  return scored.slice(0, limit).map((s) => s.a);
}
